import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import type { MenuProps } from "antd";
import { Menu, Button } from "antd";
import { getImage } from "../../../services/image-utils";
import { UserContext } from "../../../context/user.context";
import { getProfilePicture, logout } from "../../../services/users.service";
import Text from "antd/es/typography/Text";
import { BookOutlined, CaretUpOutlined, UserOutlined } from "@ant-design/icons";
import CBMenu from "../CBMenu/CBMenu";
import { Image } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useDimensions } from "../../../hooks/useDimensions";
import { useRole } from "../../../hooks/user-roles";

const Navbar = () => {
  const { user, updateUser } = useContext(UserContext);
  const { width } = useDimensions();
  const [hoverImg, setHoverImg] = useState(false);
  const [hoverText, setHoverText] = useState(false);
  const { isStaff, isSuperAdmin, isDirector, canViewAll } = useRole();

  const { data: url } = useQuery({
    queryKey: [`profile-picture`, user?.id],
    queryFn: async () => {
      if (user?.profilePicture || user?.id) {
        try {
          const result = await getProfilePicture(user?.id!);
          return result.url;
        } catch (err) {
          return getImage("/assets/profile.png");
        }
      }
    },
    staleTime: Infinity,
    enabled: !!user?.id,
  });

  const items: MenuProps["items"] = [
    {
      label: (
        <img
          style={{ marginTop: "0.5rem" }}
          src={getImage("/assets/ClassBit-Logo-navbar.png")}
          alt="Classbit Logo"
          className="h-8"
        />
      ),
      key: "home",
    },
  ];

  const MenuNavigation = () => {
    let place,
      bg,
      clr,
      hover,
      route: string,
      shows: boolean = true;
    if (window.location.href.includes("dashboard")) {
      place = "MY DASHBOARD";
      bg = "#dddddd";
      clr = "#333333";
      hover = "#dddddd";
      route = `/SEABC/dashboard`;
    } else if (window.location.href.includes("/courses")) {
      place = "ALL COURSES";
      bg = "#767676";
      clr = "white";
      hover = "#555555";
      route = `/SEABC/courses`;
    } else if (window.location.href.includes("my-courses")) {
      place = "MY COURSES";
      bg = "#faaf3a";
      clr = "white";
      hover = "#e4a033";
      route = `/SEABC/my-courses`;
    } else if (window.location.href.includes("/settings")) {
      place = "MY SETTINGS";
      bg = "#767676";
      clr = "white";
      hover = "#555555";
      route = `/SEABC/settings`;
    } else if (window.location.href.includes("/reports")) {
      place = "MY REPORTS";
      bg = "#767676";
      clr = "white";
      hover = "#82baa2";
      route = `/SEABC/reports`;
    } else if (window.location.href.includes("/my-summary")) {
      place = "MY SETTINGS";
      bg = "#29abe2";
      clr = "white";
      hover = "#269cce";
      route = `/SEABC/settings`;
    } else {
      shows = false;
    }

    return (
      shows && (
        <div
          className="flex w-56 gap-4"
          style={{
            backgroundColor: "#dddddd",
            borderRadius: "5px 5px 0px 0px",
          }}
        >
          <div className="">
            <img
              onMouseEnter={() => setHoverImg(true)}
              onMouseLeave={() => setHoverImg(false)}
              onClick={() => navigate(`/SEABC/dashboard`)}
              style={{
                height: "18px",
                marginBottom: "-6px",
                paddingLeft: "12px",
                marginRight: "-4px",
              }}
              src={getImage(
                `/assets/dashboard-${hoverImg ? "color" : "white"}.png`,
              )}
              alt="dashboard back"
            />
          </div>
          <div
            onMouseEnter={() => setHoverText(true)}
            onMouseLeave={() => setHoverText(false)}
            onClick={() => navigate(route)}
            className="flex flex-1"
            style={{
              backgroundColor: hoverText ? hover : bg,
              fontWeight: 700,
              borderLeft: "1px solid #ced4da",
              color: clr,
              letterSpacing: "1px",
              borderRadius: "0px 5px 0px 0px",
            }}
          >
            <div className="flex gap-4 pl-4">
              {!place?.includes("DASHBOARD") && (
                <img
                  style={{ height: "35px", marginTop: "5px" }}
                  src={getImage(`/assets/handbtn.png`)}
                  alt="handbtn back"
                />
              )}
              <div style={{ fontWeight: "400" }}>{place}</div>
            </div>
          </div>
        </div>
      )
    );
  };

  const logoutEvent = () => {
    logout();
    updateUser(undefined);
  };

  if (user?.id) {
    items.unshift({
      label: (
        <>
          <CBMenu />
        </>
      ),
      key: "CBMenu",
    });
    if (canViewAll || isStaff) {
      items.push({
        label: (
          <Button
            icon={<BookOutlined className="warning" />}
            iconPosition="start"
          >
            User Guides
          </Button>
        ),
        key: "SEABC/guides",
        style: { marginLeft: "auto" },
      });
    }
    items.push(
      {
        label: (
          <>
            {width >= 500 ? (
              <div className="flex text-sm gap-2 items-center">
                <Text className="font-bold" style={{ fontSize: "14px" }}>
                  {user.role === "staff"
                    ? "Team member: "
                    : isDirector
                      ? "Director: "
                      : `${user.role}: `}
                </Text>
                <Text
                  style={{ paddingRight: "5px" }}
                >{`${user.firstName} ${user.lastName}`}</Text>
                <CaretUpOutlined style={{ color: "#5ead8e" }} />
                <div style={{ maxHeight: "2.1rem", overflow: "hidden" }}>
                  <Image
                    width={35}
                    height={35}
                    preview={false}
                    src={url}
                    style={{ borderRadius: "50%" }}
                  />
                </div>
              </div>
            ) : (
              <UserOutlined style={{ fontSize: "25px" }} />
            )}
          </>
        ),
        key: "SubMenu",
        style: canViewAll || isStaff ? {} : { marginLeft: "auto" },
        children: isSuperAdmin
          ? [
              {
                type: "group",
                label: "",
                children: [
                  {
                    label: "Logout",
                    key: "SEABC/login",
                    onClick: logoutEvent,
                  },
                ],
              },
            ]
          : [
              {
                type: "group",
                label: "",
                children: [
                  {
                    label: "Settings",
                    key: "SEABC/settings",
                  },
                ],
              },
              {
                type: "group",
                label: "",
                children: [
                  {
                    label: "Logout",
                    key: "SEABC/login",
                    onClick: logoutEvent,
                  },
                ],
              },
            ],
      },
      ...(width >= 850
        ? [
            {
              label: MenuNavigation(),
              key: "divider",
              style: {
                position: "absolute" as "absolute",
                top: "1rem",
                left: "41%",
              },
            },
          ]
        : []),
    );
  } else {
    items.push(
      {
        label: "Sign Up",
        key: "SEABC/signup",
        style: { marginLeft: "auto" },
      },
      {
        label: (
          <span className="bg-slate-700 hover:bg-yellow-500 rounded py-2 px-3.5 border-none text-xs text-white">
            Login
          </span>
        ),
        key: "SEABC/login",
      },
    );
  }

  const navigate = useNavigate();
  const [current, setCurrent] = useState("mail");
  const onClick: MenuProps["onClick"] = (e) => {
    if (e.key === "home" && user) {
      const path = `/SEABC/dashboard`;
      setCurrent(e.key);
      return navigate(path);
    }

    if (e.key != "CBMenu" && e.key != "divider") {
      const path = `/${e.key}`;
      setCurrent(e.key);
      return navigate(path);
    }
  };

  return (
    <Menu
      style={{
        position: "sticky",
        top: "0",
        zIndex: "9999",
        display: "flex",
        alignItems: "baseline",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
      }}
      onClick={onClick}
      selectedKeys={[current]}
      mode="horizontal"
      items={items}
    />
  );
};

export default Navbar;
